import { useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import { Extension } from "@tiptap/core";
import { useNavigate, useParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import { Document, Packer, Paragraph } from "docx";
import { saveAs } from "file-saver";
import { api } from "../../api/api";
import gerarPeticaoInteligente from "../../utils/gerarPeticaoInteligente";

// 🔹 FontSize
const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: el => el.style.fontSize?.replace("px", ""),
            renderHTML: attr =>
              attr.fontSize
                ? { style: `font-size: ${attr.fontSize}px` }
                : {},
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        size =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: size }).run(),
    };
  },
});

// 🔹 Indent
const Indent = Extension.create({
  name: "indent",
  addOptions() {
    return { types: ["paragraph"] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textIndent: {
            default: null,
            parseHTML: el => el.style.textIndent?.replace("cm", ""),
            renderHTML: attr =>
              attr.textIndent
                ? { style: `text-indent: ${attr.textIndent}cm` }
                : {},
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setIndent:
        value =>
        ({ chain }) =>
          chain().updateAttributes("paragraph", {
            textIndent: value,
          }).run(),
      unsetIndent:
        () =>
        ({ chain }) =>
          chain().updateAttributes("paragraph", {
            textIndent: null,
          }).run(),
    };
  },
});

const EditorPeticao = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [peticao, setPeticao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conteudoGerado, setConteudoGerado] = useState(false);
  const [fontSize, setFontSize] = useState("12");

  const gerarConteudoInicial = (p) => {
    const atendimento = p.atendimento;

    return `
      <p style="text-align:center;"><strong>EXCELENTÍSSIMO SENHOR DOUTOR JUIZ</strong></p>
      <br/>
      <p><strong>Cliente:</strong> ${atendimento?.cliente?.nome || "-"}</p>
      <p><strong>CPF:</strong> ${atendimento?.cliente?.cpf || "-"}</p>
      <br/>
      <p><strong>DOS FATOS</strong></p>
      <p>[Descreva os fatos]</p>
      <br/>
      <p><strong>DO DIREITO</strong></p>
      <p>[Fundamentação jurídica]</p>
      <br/>
      <p><strong>DOS PEDIDOS</strong></p>
      <ul><li>[Pedido 1]</li></ul>
      <br/>
      <p>Termos em que, pede deferimento.</p>
    `;
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      FontSize,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Indent,
    ],
    content: "<p>Carregando...</p>",
    immediatelyRender: false,
  });

  // 🔹 Buscar dados
  useEffect(() => {
    const carregar = async () => {
      try {
        // 🔒 valida sessão
        await api.get("/auth/me");

        const res = await api.get(`/peticoes/atendimento/${id}`);
        setPeticao(res.data);

      } catch (err) {
        console.error(err);

        if (err.response?.status === 401) {
          alert("Sessão expirada");
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) carregar();
  }, [id, navigate]);

  // 🔹 Gerar conteúdo
  useEffect(() => {
    if (!editor || !peticao || conteudoGerado) return;

    try {
      if (peticao.conteudo?.trim()) {
        editor.commands.setContent(peticao.conteudo);
        setConteudoGerado(true);
        return;
      }

      const conteudo = gerarPeticaoInteligente(peticao);

      if (!conteudo?.trim()) {
        editor.commands.setContent(gerarConteudoInicial(peticao));
      } else {
        editor.commands.setContent(conteudo);
        setPeticao(prev => ({ ...prev, conteudo }));
      }

      setConteudoGerado(true);

    } catch {
      editor.commands.setContent(gerarConteudoInicial(peticao));
      setConteudoGerado(true);
    }

  }, [editor, peticao, conteudoGerado]);

  // 🔹 Font size sync
  useEffect(() => {
    if (!editor) return;

    const update = () => {
      setFontSize(editor.getAttributes("textStyle")?.fontSize || "12");
    };

    editor.on("selectionUpdate", update);
    return () => editor.off("selectionUpdate", update);
  }, [editor]);

  if (loading) return <p>Carregando...</p>;
  if (!peticao) return <p>Acesso inválido ou sessão expirada</p>;
  if (!editor) return null;

  // 💾 Salvar
  const salvarPeticao = async () => {
    try {
      await api.put(`/peticoes/${peticao._id}`, {
        conteudo: editor.getHTML(),
        status: "finalizada",
      });

      alert("Salvo com sucesso");

    } catch (err) {
      console.error(err);

      if (err.response?.status === 401) {
        alert("Sessão expirada");
        navigate("/login");
        return;
      }

      alert("Erro ao salvar");
    }
  };

  // PDF
  const gerarPDF = () => {
    const html = editor.getHTML();
    const parser = new DOMParser();
    const docHTML = parser.parseFromString(html, "text/html");

    const pdf = new jsPDF({ unit: "cm", format: "a4" });

    let y = 2.5;

    Array.from(docHTML.body.children).forEach(el => {
      const texto = el.textContent.trim();
      if (!texto) return;

      if (y > 27) {
        pdf.addPage();
        y = 2.5;
      }

      pdf.text(texto, 2.5, y);
      y += 0.7;
    });

    pdf.save("peticao.pdf");
  };

  // DOCX
  const gerarDOCX = async () => {
    const texto = editor.getText();

    const doc = new Document({
      sections: [
        {
          children: texto.split("\n").map(l => new Paragraph(l)),
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, "peticao.docx");
  };

  const voltar = () => {
    navigate("/atendimento/fila-atendimento");
    localStorage.removeItem("peticaoAtual");
  }


  return (
    <div style={{ padding: "30px" }}>
      <h2>Editor de Petição</h2>

      <div style={{ 
        marginBottom: "10px",
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "1px"
      }}>
        {/* Texto */}
        <button onClick={() => editor.chain().focus().toggleBold().run()}>
          <b>B</b>
        </button>

        <button onClick={() => editor.chain().focus().toggleItalic().run()}>
          <i>I</i>
        </button>

        <button onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <u>U</u>
        </button>

        {/* Headings */}
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          H1
        </button>

        <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          H2
        </button>

        <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          H3
        </button>

        {/* Listas */}
        <button onClick={() => editor.chain().focus().toggleBulletList().run()}>
          • Lista
        </button>

        <button onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          1. Lista
        </button>

        {/* Outros */}
        <button onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          ❝ ❞
        </button>

        <button onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
          {"</>"}
        </button>

        {/* Alinhamento */}
        <button onClick={() => editor.chain().focus().setTextAlign("left").run()}>
          ⬅
        </button>

        <button onClick={() => editor.chain().focus().setTextAlign("center").run()}>
          ⬍
        </button>

        <button onClick={() => editor.chain().focus().setTextAlign("right").run()}>
          ➡
        </button>

        <button onClick={() => editor.chain().focus().setTextAlign("justify").run()}>
          ☰
        </button>

        <button onClick={() => editor.chain().focus().setNode("paragraph", { style: "margin-left: 20px" }).run()}>
          |☰
        </button>

        <button onClick={() => editor.chain().focus().setIndent(2).run()}>
          ⮞
        </button>

        <button onClick={() => editor.chain().focus().setIndent(0).run()}>
          ⮜
        </button>

        {/* Undo / Redo */}
        <button onClick={() => editor.chain().focus().undo().run()}>
          ↺
        </button>

        <button onClick={() => editor.chain().focus().redo().run()}>
          ↻
        </button>

        {/* Font Size */}
        <select
          value={fontSize}
          onChange={e => {
            setFontSize(e.target.value);
            editor.chain().focus().setFontSize(e.target.value).run();
          }}
        >
          {[...Array(33)].map((_, i) => {
            const size = 8 + i * 2;
            return (
              <option key={size} value={size}>
                {size}px
              </option>
            );
          })}
        </select>
      </div>

      <div style={{
        maxWidth: "800px",
        margin: "auto",
        borderBottom: "1px solid #ccc",
        position: "relative",
        height: "30px"
      }}>
        {[...Array(21)].map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            left: `${i * 5}%`,
            fontSize: "10px"
          }}>
            {i}
          </div>
        ))}
      </div>

      <div
        style={{
          border: "1px solid #ccc",
          minHeight: "500px",
          margin: "auto",
          backgroundColor: "white",
          maxWidth: "794px", // A4 aproximado
          padding: "40px",
          lineHeight: "1.6",
          fontFamily: "Times New Roman",
        }}
      >
        <EditorContent editor={editor} />
      </div>

      <button onClick={salvarPeticao}>Salvar Petição</button>
      <button onClick={gerarPDF}>Gerar PDF</button>
      <button onClick={gerarDOCX}>Gerar DOCX</button>
      <button onClick={voltar}>Voltar</button>
    </div>
  );
};

export default EditorPeticao;